"use client";

import { useState, type ReactNode } from "react";
import {
  Percent,
  Calculator,
  RotateCcw,
  TrendingUp,
  PieChart,
  ArrowUpDown,
  Tag,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Separator } from "@/app/components/ui/separator";

interface CalculationResult {
  result: number;
  formula: string;
  explanation: string;
  /** Optional second metric (e.g. amount saved on discount) */
  secondary?: { label: string; value: string };
  /** How to format the primary number */
  format?: "plain" | "percent" | "currency";
}

function ResultPanel({
  hasResult,
  emptyHint,
  children,
}: {
  hasResult: boolean;
  emptyHint: string;
  children: ReactNode;
}) {
  return (
    <div
      className="flex min-h-[220px] flex-col rounded-2xl border border-border/80 bg-muted/30 p-5 shadow-inner dark:bg-slate-950/30"
      aria-live="polite"
    >
      {hasResult ? (
        children
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-2 text-center">
          <div className="rounded-full bg-muted p-3 dark:bg-slate-800/80">
            <Calculator className="h-8 w-8 text-muted-foreground" aria-hidden />
          </div>
          <p className="max-w-[18rem] text-sm text-muted-foreground">{emptyHint}</p>
        </div>
      )}
    </div>
  );
}

function ResultHero({
  label,
  value,
  variant = "neutral",
  format = "plain",
}: {
  label: string;
  value: number;
  variant?: "neutral" | "up" | "down";
  format?: "plain" | "percent" | "currency";
}) {
  const formatted =
    format === "percent"
      ? `${variant !== "neutral" && value > 0 ? "+" : ""}${value.toFixed(2)}%`
      : format === "currency"
        ? `$${value.toFixed(2)}`
        : value.toFixed(2);

  const tone =
    variant === "up"
      ? "text-emerald-600 dark:text-emerald-400"
      : variant === "down"
        ? "text-rose-600 dark:text-rose-400"
        : "text-foreground";

  return (
    <div className="text-center">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className={`mt-1 text-4xl font-semibold tabular-nums tracking-tight ${tone}`}>{formatted}</p>
    </div>
  );
}

function ResultDetails({ formula, explanation }: { formula: string; explanation: string }) {
  return (
    <div className="space-y-3 pt-2">
      <Separator />
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Formula</p>
        <p className="mt-1 rounded-lg bg-background/80 px-3 py-2 font-mono text-xs leading-relaxed text-muted-foreground dark:bg-slate-900/80">
          {formula}
        </p>
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Summary</p>
        <p className="mt-1 text-sm leading-relaxed text-foreground/90">{explanation}</p>
      </div>
    </div>
  );
}

export default function PercentagePage() {
  const [activeTab, setActiveTab] = useState("basic");

  const [basicValue, setBasicValue] = useState("");
  const [basicPercent, setBasicPercent] = useState("");
  const [basicResult, setBasicResult] = useState<CalculationResult | null>(null);

  const [partValue, setPartValue] = useState("");
  const [totalValue, setTotalValue] = useState("");
  const [percentageResult, setPercentageResult] = useState<CalculationResult | null>(null);

  const [oldValue, setOldValue] = useState("");
  const [newValue, setNewValue] = useState("");
  const [changeResult, setChangeResult] = useState<CalculationResult | null>(null);

  const [originalValue, setOriginalValue] = useState("");
  const [changePercent, setChangePercent] = useState("");
  const [increaseResult, setIncreaseResult] = useState<CalculationResult | null>(null);

  const [originalPrice, setOriginalPrice] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [discountResult, setDiscountResult] = useState<CalculationResult | null>(null);

  const calculateBasicPercentage = () => {
    const value = parseFloat(basicValue);
    const percent = parseFloat(basicPercent);

    if (isNaN(value) || isNaN(percent)) return;

    const result = (value * percent) / 100;
    const formula = `${value} × ${percent}% = ${value} × ${percent / 100} = ${result}`;
    const explanation = `${percent}% of ${value} is ${result}.`;

    setBasicResult({ result, formula, explanation, format: "plain" });
  };

  const calculatePercentageOfTotal = () => {
    const part = parseFloat(partValue);
    const total = parseFloat(totalValue);

    if (isNaN(part) || isNaN(total) || total === 0) return;

    const result = (part / total) * 100;
    const formula = `(${part} ÷ ${total}) × 100 = ${(part / total).toFixed(4)} × 100 = ${result.toFixed(2)}%`;
    const explanation = `${part} is ${result.toFixed(2)}% of ${total}.`;

    setPercentageResult({ result, formula, explanation, format: "percent" });
  };

  const calculatePercentageChange = () => {
    const old = parseFloat(oldValue);
    const newVal = parseFloat(newValue);

    if (isNaN(old) || isNaN(newVal) || old === 0) return;

    const change = newVal - old;
    const result = (change / old) * 100;
    const formula = `((${newVal} - ${old}) ÷ ${old}) × 100 = (${change} ÷ ${old}) × 100 = ${result.toFixed(2)}%`;
    const explanation = `The value changed from ${old} to ${newVal}, which is ${result >= 0 ? "an increase" : "a decrease"} of ${Math.abs(result).toFixed(2)}%.`;

    setChangeResult({ result, formula, explanation, format: "percent" });
  };

  const calculatePercentageIncrease = () => {
    const original = parseFloat(originalValue);
    const percent = parseFloat(changePercent);

    if (isNaN(original) || isNaN(percent)) return;

    const change = (original * percent) / 100;
    const result = original + change;
    const formula = `${original} + (${original} × ${percent}%) = ${original} + ${change} = ${result}`;
    const explanation = `After a ${percent}% ${percent >= 0 ? "increase" : "decrease"}, the value becomes ${result}.`;

    setIncreaseResult({ result, formula, explanation, format: "plain" });
  };

  const calculateDiscount = () => {
    const price = parseFloat(originalPrice);
    const discount = parseFloat(discountPercent);

    if (isNaN(price) || isNaN(discount)) return;

    const discountAmount = (price * discount) / 100;
    const finalPrice = price - discountAmount;
    const formula = `${price} - (${price} × ${discount}%) = ${price} - ${discountAmount} = ${finalPrice}`;
    const explanation = `With a ${discount}% discount, you save $${discountAmount.toFixed(2)} and pay $${finalPrice.toFixed(2)}.`;

    setDiscountResult({
      result: finalPrice,
      formula,
      explanation,
      format: "currency",
      secondary: { label: "You save", value: `$${discountAmount.toFixed(2)}` },
    });
  };

  const clearAll = () => {
    setBasicValue("");
    setBasicPercent("");
    setBasicResult(null);
    setPartValue("");
    setTotalValue("");
    setPercentageResult(null);
    setOldValue("");
    setNewValue("");
    setChangeResult(null);
    setOriginalValue("");
    setChangePercent("");
    setIncreaseResult(null);
    setOriginalPrice("");
    setDiscountPercent("");
    setDiscountResult(null);
  };

  const numberInputProps = {
    type: "number" as const,
    inputMode: "decimal" as const,
    className:
      "h-11 tabular-nums transition-shadow focus-visible:ring-2 focus-visible:ring-primary/30",
  };

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8 md:px-6">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-primary">
            <Percent className="h-8 w-8 shrink-0" aria-hidden />
            <h1 className="text-3xl font-bold tracking-tight">Percentage calculator</h1>
          </div>
          <p className="max-w-2xl text-muted-foreground">
            Find a percent of a number, share of a total, percent change, adjusted values, and sale prices—with the
            math shown step by step.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={clearAll} className="shrink-0 gap-2 self-start">
          <RotateCcw className="h-4 w-4" aria-hidden />
          Clear all
        </Button>
      </div>

      <Card className="overflow-hidden border-border/60 shadow-lg">
        <CardHeader className="border-b border-border/60 bg-muted/20 px-4 py-5 sm:px-6">
          <CardTitle className="text-lg">Modes</CardTitle>
          <CardDescription>Pick a mode, enter numbers, then press Calculate or Enter.</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <TabsList className="inline-flex h-auto min-w-full flex-nowrap justify-start gap-1 rounded-xl bg-muted/60 p-1.5 sm:grid sm:min-w-0 sm:grid-cols-5">
                <TabsTrigger
                  value="basic"
                  className="gap-1.5 whitespace-nowrap rounded-lg px-3 py-2.5 text-xs font-medium data-[state=active]:shadow-sm sm:text-sm"
                >
                  <Percent className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                  Basic
                </TabsTrigger>
                <TabsTrigger
                  value="percentage"
                  className="gap-1.5 whitespace-nowrap rounded-lg px-3 py-2.5 text-xs font-medium data-[state=active]:shadow-sm sm:text-sm"
                >
                  <PieChart className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                  % of total
                </TabsTrigger>
                <TabsTrigger
                  value="change"
                  className="gap-1.5 whitespace-nowrap rounded-lg px-3 py-2.5 text-xs font-medium data-[state=active]:shadow-sm sm:text-sm"
                >
                  <ArrowUpDown className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                  % change
                </TabsTrigger>
                <TabsTrigger
                  value="increase"
                  className="gap-1.5 whitespace-nowrap rounded-lg px-3 py-2.5 text-xs font-medium data-[state=active]:shadow-sm sm:text-sm"
                >
                  <TrendingUp className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                  Adjust %
                </TabsTrigger>
                <TabsTrigger
                  value="discount"
                  className="gap-1.5 whitespace-nowrap rounded-lg px-3 py-2.5 text-xs font-medium data-[state=active]:shadow-sm sm:text-sm"
                >
                  <Tag className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                  Discount
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="basic" className="mt-6 space-y-4 outline-none">
              <p className="text-sm text-muted-foreground">
                What is <span className="font-medium text-foreground">P%</span> of a number{" "}
                <span className="font-medium text-foreground">V</span>?
              </p>
              <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    calculateBasicPercentage();
                  }}
                >
                  <div className="space-y-2">
                    <Label htmlFor="basic-value">Value (V)</Label>
                    <Input
                      id="basic-value"
                      {...numberInputProps}
                      placeholder="e.g. 200"
                      value={basicValue}
                      onChange={(e) => setBasicValue(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="basic-percent">Percent (P)</Label>
                    <Input
                      id="basic-percent"
                      {...numberInputProps}
                      placeholder="e.g. 15"
                      value={basicPercent}
                      onChange={(e) => setBasicPercent(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <Button type="submit" className="h-11 w-full gap-2">
                    <Calculator className="h-4 w-4" aria-hidden />
                    Calculate
                  </Button>
                </form>
                <ResultPanel
                  hasResult={!!basicResult}
                  emptyHint="Enter a value and a percent, then calculate to see the result and formula."
                >
                  {basicResult && (
                    <div className="space-y-4">
                      <ResultHero label="Result" value={basicResult.result} format={basicResult.format} />
                      <ResultDetails formula={basicResult.formula} explanation={basicResult.explanation} />
                    </div>
                  )}
                </ResultPanel>
              </div>
            </TabsContent>

            <TabsContent value="percentage" className="mt-6 space-y-4 outline-none">
              <p className="text-sm text-muted-foreground">
                What percent is the <span className="font-medium text-foreground">part</span> of the{" "}
                <span className="font-medium text-foreground">whole</span>?
              </p>
              <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    calculatePercentageOfTotal();
                  }}
                >
                  <div className="space-y-2">
                    <Label htmlFor="part-value">Part</Label>
                    <Input
                      id="part-value"
                      {...numberInputProps}
                      placeholder="e.g. 25"
                      value={partValue}
                      onChange={(e) => setPartValue(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="total-value">Total</Label>
                    <Input
                      id="total-value"
                      {...numberInputProps}
                      placeholder="e.g. 200"
                      value={totalValue}
                      onChange={(e) => setTotalValue(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <Button type="submit" className="h-11 w-full gap-2">
                    <Calculator className="h-4 w-4" aria-hidden />
                    Calculate
                  </Button>
                </form>
                <ResultPanel
                  hasResult={!!percentageResult}
                  emptyHint="Enter part and total values to see what percent the part is of the whole."
                >
                  {percentageResult && (
                    <div className="space-y-4">
                      <ResultHero label="Share of total" value={percentageResult.result} format="percent" />
                      <ResultDetails formula={percentageResult.formula} explanation={percentageResult.explanation} />
                    </div>
                  )}
                </ResultPanel>
              </div>
            </TabsContent>

            <TabsContent value="change" className="mt-6 space-y-4 outline-none">
              <p className="text-sm text-muted-foreground">
                Percent change from an <span className="font-medium text-foreground">old</span> value to a{" "}
                <span className="font-medium text-foreground">new</span> value.
              </p>
              <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    calculatePercentageChange();
                  }}
                >
                  <div className="space-y-2">
                    <Label htmlFor="old-value">Old value</Label>
                    <Input
                      id="old-value"
                      {...numberInputProps}
                      placeholder="e.g. 80"
                      value={oldValue}
                      onChange={(e) => setOldValue(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-value">New value</Label>
                    <Input
                      id="new-value"
                      {...numberInputProps}
                      placeholder="e.g. 100"
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <Button type="submit" className="h-11 w-full gap-2">
                    <Calculator className="h-4 w-4" aria-hidden />
                    Calculate
                  </Button>
                </form>
                <ResultPanel
                  hasResult={!!changeResult}
                  emptyHint="Enter old and new values. Old value cannot be zero."
                >
                  {changeResult && (
                    <div className="space-y-4">
                      <ResultHero
                        label="Percent change"
                        value={changeResult.result}
                        format="percent"
                        variant={changeResult.result >= 0 ? "up" : "down"}
                      />
                      <ResultDetails formula={changeResult.formula} explanation={changeResult.explanation} />
                    </div>
                  )}
                </ResultPanel>
              </div>
            </TabsContent>

            <TabsContent value="increase" className="mt-6 space-y-4 outline-none">
              <p className="text-sm text-muted-foreground">
                Increase or decrease a value by a given percent (use a negative percent to decrease).
              </p>
              <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    calculatePercentageIncrease();
                  }}
                >
                  <div className="space-y-2">
                    <Label htmlFor="original-value">Starting value</Label>
                    <Input
                      id="original-value"
                      {...numberInputProps}
                      placeholder="e.g. 50"
                      value={originalValue}
                      onChange={(e) => setOriginalValue(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="change-percent">Percent change</Label>
                    <Input
                      id="change-percent"
                      {...numberInputProps}
                      placeholder="e.g. 10 or -10"
                      value={changePercent}
                      onChange={(e) => setChangePercent(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <Button type="submit" className="h-11 w-full gap-2">
                    <Calculator className="h-4 w-4" aria-hidden />
                    Calculate
                  </Button>
                </form>
                <ResultPanel
                  hasResult={!!increaseResult}
                  emptyHint="Enter a starting value and a percent change to see the new value."
                >
                  {increaseResult && (
                    <div className="space-y-4">
                      <ResultHero label="New value" value={increaseResult.result} format={increaseResult.format} />
                      <ResultDetails formula={increaseResult.formula} explanation={increaseResult.explanation} />
                    </div>
                  )}
                </ResultPanel>
              </div>
            </TabsContent>

            <TabsContent value="discount" className="mt-6 space-y-4 outline-none">
              <p className="text-sm text-muted-foreground">
                Sale price after a percent-off discount from the original price.
              </p>
              <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    calculateDiscount();
                  }}
                >
                  <div className="space-y-2">
                    <Label htmlFor="original-price">Original price</Label>
                    <Input
                      id="original-price"
                      {...numberInputProps}
                      placeholder="e.g. 99.99"
                      value={originalPrice}
                      onChange={(e) => setOriginalPrice(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="discount-percent">Discount (%)</Label>
                    <Input
                      id="discount-percent"
                      {...numberInputProps}
                      placeholder="e.g. 20"
                      value={discountPercent}
                      onChange={(e) => setDiscountPercent(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <Button type="submit" className="h-11 w-full gap-2">
                    <Calculator className="h-4 w-4" aria-hidden />
                    Calculate
                  </Button>
                </form>
                <ResultPanel
                  hasResult={!!discountResult}
                  emptyHint="Enter price and discount percent to see the final price and amount saved."
                >
                  {discountResult && (
                    <div className="space-y-4">
                      <ResultHero label="You pay" value={discountResult.result} format={discountResult.format} />
                      {discountResult.secondary && (
                        <p className="text-center text-sm text-muted-foreground">
                          <span className="font-medium text-foreground">{discountResult.secondary.label}:</span>{" "}
                          <span className="tabular-nums text-emerald-600 dark:text-emerald-400">
                            {discountResult.secondary.value}
                          </span>
                        </p>
                      )}
                      <ResultDetails formula={discountResult.formula} explanation={discountResult.explanation} />
                    </div>
                  )}
                </ResultPanel>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
